import { useNavigate } from "react-router-dom";
import MarketingNav from "./MarketingNav";

// ─────────────────────────────────────────────────────────────────────────────
// LifeDesk Landing Page
// Drop product screenshots into /public/images/ — see image src paths below
// ─────────────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div style={{ background: "#fff", color: "#0F172A", fontFamily: "'DM Sans', 'Segoe UI', sans-serif", overflowX: "hidden" }}>

      {/* Mobile-responsive overrides */}
      <style>{`
        @media (max-width: 900px) {
          .ld-hero { flex-direction: column !important; padding: 32px 20px 0 !important; gap: 32px !important; }
          .ld-hero-text { max-width: 100% !important; text-align: left !important; }
          .ld-hero-headline { font-size: 38px !important; line-height: 1.15 !important; }
          .ld-hero-subhead { font-size: 15px !important; }
          .ld-cta-row { flex-direction: column !important; align-items: stretch !important; }
          .ld-cta-row > button { width: 100% !important; justify-content: center !important; }
          .ld-features { padding: 64px 20px 72px !important; }
          .ld-feature-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
          .ld-hero-image { max-width: 100% !important; }
        }
        @media (max-width: 480px) {
          .ld-hero-headline { font-size: 32px !important; }
        }
      `}</style>

      {/* ── Navigation ───────────────────────────────────────────────────── */}
      <MarketingNav />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="ld-hero" style={{
        display: "flex", maxWidth: 1400, margin: "0 auto",
        padding: "60px 40px 80px", gap: 60, alignItems: "center",
      }}>
        {/* Left — Copy */}
        <div className="ld-hero-text" style={{ flex: 1, maxWidth: 580 }}>
          {/* Eyebrow pill */}
          <div style={{
            display: "inline-block", background: "#EFF6FF", color: "#1E40AF",
            borderRadius: 20, padding: "6px 14px", fontSize: 11, fontWeight: 700,
            letterSpacing: 1.5, marginBottom: 24,
          }}>
            YOUR LIFE. ORGANIZED.
          </div>

          {/* Headline */}
          <h1 className="ld-hero-headline" style={{
            fontSize: 54, fontWeight: 800, lineHeight: 1.08, color: "#0F172A",
            marginBottom: 22, letterSpacing: -1.5,
          }}>
            Turn your to-do list<br />
            into a{" "}
            <span style={{ position: "relative", color: "#2563EB", whiteSpace: "nowrap" }}>
              clear workflow.
              {/* Hand-drawn-style cyan underline */}
              <svg
                viewBox="0 0 300 14" preserveAspectRatio="none"
                style={{ position: "absolute", left: 0, right: 0, bottom: -6, width: "100%", height: 12, display: "block" }}
              >
                <path
                  d="M 4 8 Q 75 2, 150 6 T 296 7"
                  stroke="#06B6D4" strokeWidth="3.5" strokeLinecap="round" fill="none"
                />
              </svg>
            </span>
          </h1>

          {/* Subheadline */}
          <p className="ld-hero-subhead" style={{
            fontSize: 17, color: "#64748B", lineHeight: 1.6, marginBottom: 32, maxWidth: 540,
          }}>
            LifeDesk combines your personal to-dos with a smart ticket queue so
            you can track what matters, prioritize with clarity, and make steady
            progress every day.
          </p>

          {/* CTAs */}
          <div className="ld-cta-row" style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 18 }}>
            <button onClick={() => navigate("/app")} style={{
              background: "#2563EB", color: "#fff", border: "none", borderRadius: 11,
              padding: "15px 28px", fontWeight: 700, fontSize: 15, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 10, fontFamily: "inherit",
              boxShadow: "0 4px 16px rgba(37,99,235,.3)",
              transition: "transform .15s, box-shadow .15s",
            }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(37,99,235,.4)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 16px rgba(37,99,235,.3)"; }}
            >
              Get started for free <span style={{ fontSize: 16 }}>→</span>
            </button>

            <button onClick={() => navigate("/coming-soon")} style={{
              background: "#fff", color: "#0F172A", border: "1.5px solid #E2E8F0",
              borderRadius: 11, padding: "13px 22px", fontWeight: 600, fontSize: 15,
              cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10,
              fontFamily: "inherit",
            }}>
              <span style={{
                width: 24, height: 24, borderRadius: "50%", background: "#0F172A",
                color: "#fff", display: "inline-flex", alignItems: "center",
                justifyContent: "center", fontSize: 9, paddingLeft: 2,
              }}>▶</span>
              Watch demo
            </button>
          </div>

          {/* Trust line */}
          <div style={{ fontSize: 13, color: "#64748B", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{
              width: 16, height: 16, borderRadius: "50%", background: "#22C55E",
              color: "#fff", display: "inline-flex", alignItems: "center",
              justifyContent: "center", fontSize: 10, fontWeight: 800,
            }}>✓</span>
            No credit card required · Setup in 30 seconds
          </div>
        </div>

        {/* Right — Product preview image */}
        <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <img
            src="/images/landing-hero.png"
            alt="LifeDesk app preview"
            className="ld-hero-image"
            style={{
              maxWidth: 640, width: "100%", height: "auto",
              borderRadius: 14,
              filter: "drop-shadow(0 25px 50px rgba(15,23,42,.18))",
            }}
            onError={(e) => {
              // Graceful fallback if the image isn't uploaded yet
              e.target.outerHTML = `
                <div style="
                  width: 100%; max-width: 640px; aspect-ratio: 5/4;
                  background: linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%);
                  border: 2px dashed #93C5FD; border-radius: 14px;
                  display: flex; flex-direction: column; align-items: center; justify-content: center;
                  color: #1E40AF; font-weight: 600; gap: 8px; padding: 24px; text-align: center;
                ">
                  <div style="font-size: 32px;">🖼️</div>
                  <div style="font-size: 15px;">Drop your hero screenshot here</div>
                  <div style="font-size: 12px; color: #3B82F6; font-family: monospace;">/public/images/landing-hero.png</div>
                </div>
              `;
            }}
          />
        </div>
      </section>

      {/* ── Curved transition into dark section ──────────────────────────── */}
      <div style={{ position: "relative", lineHeight: 0, marginBottom: -1 }}>
        <svg viewBox="0 0 1440 100" preserveAspectRatio="none" style={{ display: "block", width: "100%", height: 80 }}>
          <path d="M0,40 C360,100 1080,0 1440,60 L1440,100 L0,100 Z" fill="#0F2A6E" />
        </svg>
      </div>

      {/* ── Features (dark blue) ─────────────────────────────────────────── */}
      <section id="features" className="ld-features" style={{
        background: "linear-gradient(180deg, #0F2A6E 0%, #1E3A8A 100%)",
        color: "#fff", padding: "60px 40px 90px",
      }}>
        <div className="ld-feature-grid" style={{
          maxWidth: 1200, margin: "0 auto",
          display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
          gap: 60, alignItems: "flex-start",
        }}>
          {[
            {
              icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#60A5FA" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="8" y1="6" x2="21" y2="6" />
                  <line x1="8" y1="12" x2="21" y2="12" />
                  <line x1="8" y1="18" x2="21" y2="18" />
                  <circle cx="3.5" cy="6" r="1.2" fill="#60A5FA" />
                  <circle cx="3.5" cy="12" r="1.2" fill="#60A5FA" />
                  <circle cx="3.5" cy="18" r="1.2" fill="#60A5FA" />
                </svg>
              ),
              title: "Stay organized",
              body:  "Keep all your tasks and tickets in one place.",
            },
            {
              icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#60A5FA" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="6" />
                  <circle cx="12" cy="12" r="2" fill="#60A5FA" />
                </svg>
              ),
              title: "Focus better",
              body:  "Prioritize what matters and eliminate distractions.",
            },
            {
              icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#60A5FA" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="6"  y1="20" x2="6"  y2="13" />
                  <line x1="12" y1="20" x2="12" y2="8"  />
                  <line x1="18" y1="20" x2="18" y2="4"  />
                </svg>
              ),
              title: "Track everything",
              body:  "See progress, meet goals, and never miss a thing.",
            },
          ].map((f, i) => (
            <div key={i} style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
              <div style={{
                width: 50, height: 50, background: "rgba(96,165,250,.12)",
                border: "1px solid rgba(96,165,250,.25)", borderRadius: 11,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                {f.icon}
              </div>
              <div>
                <h3 style={{ fontSize: 19, fontWeight: 700, marginBottom: 6, color: "#fff", letterSpacing: -0.3 }}>
                  {f.title}
                </h3>
                <p style={{ fontSize: 14, color: "#CBD5E1", lineHeight: 1.55, margin: 0 }}>
                  {f.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
